import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, BoxIcon, HardHatIcon, PowerIcon, ClipboardCheckIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Road & Transport
{
  id: 'rt1',
  title: 'Road Maintenance Request',
  description: 'Report potholes, road damage, or request repairs',
  category: 'Road & Transport',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
}, {
  id: 'rt2',
  title: 'Traffic Signal Service',
  description: 'Report malfunctioning traffic lights or signal timing issues',
  category: 'Road & Transport',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'rt3',
  title: 'Street Lighting Request',
  description: 'Request new street light installation or report outages',
  category: 'Road & Transport',
  estimatedTime: '5-7 business days',
  fee: 'Varies by service'
}, {
  id: 'rt4',
  title: 'Parking Infrastructure',
  description: 'Request parking meters, signs, or zone modifications',
  category: 'Road & Transport',
  estimatedTime: '10-15 business days',
  fee: 'Based on request'
},
// Public Works
{
  id: 'pw1',
  title: 'Sidewalk Repair',
  description: 'Report damaged sidewalks or request new installation',
  category: 'Public Works',
  estimatedTime: '7-10 business days',
  fee: 'Assessment required'
}, {
  id: 'pw2',
  title: 'Bridge Maintenance',
  description: 'Report bridge-related issues or concerns',
  category: 'Public Works',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'pw3',
  title: 'Public Space Enhancement',
  description: 'Request improvements to parks, plazas, or public areas',
  category: 'Public Works',
  estimatedTime: '15-20 business days',
  fee: 'Project dependent'
}, {
  id: 'pw4',
  title: 'Storm Infrastructure',
  description: 'Report issues with storm drains or flood control systems',
  category: 'Public Works',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
},
// Utilities & Networks
{
  id: 'un1',
  title: 'Underground Service Marking',
  description: 'Request utility line marking for construction/excavation',
  category: 'Utilities & Networks',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'un2',
  title: 'Fiber Network Extension',
  description: 'Apply for municipal fiber network connection',
  category: 'Utilities & Networks',
  estimatedTime: '10-15 business days',
  fee: '$300.00'
}, {
  id: 'un3',
  title: 'Smart City Infrastructure',
  description: 'Request installation of smart city sensors or equipment',
  category: 'Utilities & Networks',
  estimatedTime: '15-20 business days',
  fee: 'Project based'
}, {
  id: 'un4',
  title: 'Public WiFi Service',
  description: 'Report issues or request public WiFi infrastructure',
  category: 'Utilities & Networks',
  estimatedTime: '5-7 business days',
  fee: 'No fee'
},
// Permits & Inspections
{
  id: 'pi1',
  title: 'Road Work Permit',
  description: 'Apply for permit to conduct road work or modifications',
  category: 'Permits & Inspections',
  estimatedTime: '5-7 business days',
  fee: '$200.00'
}, {
  id: 'pi2',
  title: 'Infrastructure Inspection',
  description: 'Schedule inspection of public infrastructure',
  category: 'Permits & Inspections',
  estimatedTime: '3-5 business days',
  fee: '$150.00'
}, {
  id: 'pi3',
  title: 'Construction Impact Assessment',
  description: 'Review impact of construction on public infrastructure',
  category: 'Permits & Inspections',
  estimatedTime: '7-10 business days',
  fee: '$250.00'
}, {
  id: 'pi4',
  title: 'Utility Work Coordination',
  description: 'Coordinate multiple utility work permits',
  category: 'Permits & Inspections',
  estimatedTime: '5-7 business days',
  fee: '$175.00'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Road & Transport':
      return <div className="h-6 w-6 text-blue-600" data-id="element-3844" />;
    case 'Public Works':
      return <HardHatIcon className="h-6 w-6 text-green-600" data-id="element-3845" />;
    case 'Utilities & Networks':
      return <PowerIcon className="h-6 w-6 text-purple-600" data-id="element-3846" />;
    case 'Permits & Inspections':
      return <ClipboardCheckIcon className="h-6 w-6 text-orange-600" data-id="element-3847" />;
    default:
      return <BoxIcon className="h-6 w-6 text-gray-600" data-id="element-3848" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3849">
      <div className="p-5 flex flex-col h-full" data-id="element-3850">
        <div className="flex-1" data-id="element-3851">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3852">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3853">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3854">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3855">
            <div className="flex items-center" data-id="element-3856">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3857">
                ⏱
              </span>
              <span data-id="element-3858">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3859">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3860">
                💰
              </span>
              <span data-id="element-3861">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3862">
            Submit Request
            <span className="ml-1" data-id="element-3863">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3864">
      <div className="flex items-center mb-4" data-id="element-3865">
        <CategoryIcon category={category} data-id="element-3866" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3867">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3868">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3869" />)}
      </div>
    </div>;
};
export const InfrastructurePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3870">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3871">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3872">
          <a href="/" className="hover:text-gray-700" data-id="element-3873">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3874" />
          <span className="text-gray-900" data-id="element-3875">Infrastructure Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3876">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3877">
            Infrastructure Services
          </h1>
          <p className="text-gray-600" data-id="element-3878">
            Manage and maintain public infrastructure, utilities, and
            transportation systems
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3879">
          <div className="max-w-md relative" data-id="element-3880">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3881">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3882" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3883" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3884" />)}
      </div>
    </div>;
};